import type { IAiNamingService, INameRequest, INameResult } from '../../src/ai/AiNaming';
import type { IAiTaggingService, IKnownNames, ITagProposal, ITagRequest } from '../../src/tagging/Tagging';
import type { IGraphClient } from '../../src/services/GraphService';
import { getToken } from './auth';
import { API_SCOPES } from './config';

/**
 * File types whose text the backend can read: plain text, plus Word, Excel, PowerPoint and PDF
 * (text extracted server-side, first ~2,000 characters). Others are named from their name and folder only.
 */
const TEXT_TYPES: string[] = [
  'txt', 'md', 'csv', 'json', 'log', 'xml', 'html', 'htm',
  'docx', 'docm', 'dotx', 'xlsx', 'xlsm', 'pptx', 'pptm', 'pdf'
];

async function callApi<T>(path: string, init?: RequestInit, interactive: boolean = true): Promise<T> {
  const token: string = await getToken(API_SCOPES, interactive);
  const response: Response = await fetch(`/api/${path}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), 'x-organizer-token': token, 'Content-Type': 'application/json' }
  });
  const body: { error?: string } & T = await response.json().catch(() => ({}) as { error?: string } & T);
  if (!response.ok) {
    throw new Error(body.error ?? `The AI service returned an error (${response.status}).`);
  }
  return body;
}

/** Balance for the top bar. Never prompts: until AI features are first used, it just isn't shown. */
/** Diagnostics only: why the AI did or didn't get text for each file (no names or content). */
function logExcerptStatuses(results: { excerptStatus?: string }[]): void {
  const statuses: { [status: string]: number } = {};
  for (const r of results) {
    const key: string = r.excerptStatus ?? 'not-requested';
    statuses[key] = (statuses[key] ?? 0) + 1;
  }
  console.info('[SharePointOrganizer] AI excerpt status per file:', statuses);
}

export async function fetchCredits(): Promise<{ balance: number; provider: string; dataResidency?: 'global' | 'us' }> {
  return callApi('credits', undefined, false);
}

/**
 * AI naming through our backend. For text files the browser passes a short-lived download
 * link; the backend reads only the first ~8 KB and uses at most 2,000 characters. Documents
 * are never uploaded or stored whole.
 */
export class AiClient implements IAiNamingService, IAiTaggingService {
  public readonly creditsPerFile: number = 1;

  constructor(private readonly _graph: IGraphClient, private readonly _onBalance: (balance: number) => void) {}

  public async suggestNames(driveId: string, requests: INameRequest[], known?: IKnownNames): Promise<INameResult> {
    const files = await Promise.all(requests.map(async r => ({
      id: r.changeId,
      name: r.name,
      extension: r.extension,
      folderPath: r.folderPath,
      // A short-lived, pre-authorized link: the backend reads the first few KB from it.
      downloadUrl: TEXT_TYPES.indexOf(r.extension) !== -1 ? await this._downloadUrl(driveId, r.fileId) : undefined
    })));
    const result = await callApi<{
      results: { id: string; suggestedName?: string; confidence: number; reason: string; excerptStatus?: string }[];
      creditsUsed: number;
      balance: number;
    }>('ai/suggest-names', { method: 'POST', body: JSON.stringify({ files, known }) });
    this._onBalance(result.balance);
    logExcerptStatuses(result.results);
    return {
      suggestions: result.results
        .filter(s => !!s.suggestedName)
        .map(s => ({ changeId: s.id, name: s.suggestedName!, confidence: s.confidence, reason: s.reason })),
      creditsUsed: result.creditsUsed,
      balance: result.balance
    };
  }

  public async suggestTags(driveId: string, requests: ITagRequest[], known?: IKnownNames): Promise<{ proposals: ITagProposal[]; creditsUsed: number; balance?: number }> {
    const files = await Promise.all(requests.map(async r => ({
      id: r.fileId,
      name: r.name,
      extension: r.extension,
      folderPath: r.folderPath,
      downloadUrl: TEXT_TYPES.indexOf(r.extension) !== -1 ? await this._downloadUrl(driveId, r.fileId) : undefined
    })));
    const result = await callApi<{
      results: { id: string; tags: ITagProposal['tags']; confidence: number; reason: string; excerptStatus?: string }[];
      creditsUsed: number;
      balance: number;
    }>('ai/suggest-tags', { method: 'POST', body: JSON.stringify({ files, known }) });
    this._onBalance(result.balance);
    logExcerptStatuses(result.results);
    return {
      proposals: result.results.map(r => ({ fileId: r.id, tags: r.tags, confidence: r.confidence, reason: r.reason })),
      creditsUsed: result.creditsUsed,
      balance: result.balance
    };
  }

  private async _downloadUrl(driveId: string, fileId: string): Promise<string | undefined> {
    try {
      // The full item: Graph includes the pre-authorized download link for files by default
      // (asking for it by name in $select can come back without it).
      const item: { '@microsoft.graph.downloadUrl'?: string } = await this._graph.api(`/drives/${driveId}/items/${fileId}`).get();
      const url: string | undefined = item['@microsoft.graph.downloadUrl'];
      if (!url) {
        console.warn('[SharePointOrganizer] Microsoft Graph returned no download link for a file.');
      }
      return url;
    } catch (error) {
      console.warn('[SharePointOrganizer] Could not get a download link; naming from name and folder only.', error);
      return undefined;
    }
  }
}
